/**
 * Single source of truth for MyU's `userTypeId` enum and its human labels.
 *
 * Backend `user_type` table:
 *   1 → Faculty   (Teacher or Professor)        → instructor
 *   2 → Student                                 → student
 *   3 → Club      (Department / Management)     → management
 *   4 → Promoter                                → promoter
 *   5 → Parents                                 → parent
 *   6 → Tutors                                  → tutor
 *
 * Any place in the app that needs to interpret `userTypeId` (profile,
 * connections, class members, feed authors, …) MUST import from this file
 * instead of redefining its own mapping.
 */

import { ACCOUNT_TYPE_LABELS } from '@/features/profile/constants';
import type { UserAccountType } from '@/features/profile/types';

/** Numeric `userTypeId` → canonical `UserAccountType` discriminator. */
export const USER_TYPE_ID_TO_ACCOUNT_TYPE: Readonly<Record<number, UserAccountType>> = {
  1: 'instructor',
  2: 'student',
  3: 'management',
  4: 'promoter',
  5: 'parent',
  6: 'tutor',
};

/**
 * Resolve the `UserAccountType` for a numeric `userTypeId`.
 * Returns `undefined` for unknown / missing values.
 */
export function getAccountTypeFromUserTypeId(
  userTypeId: number | null | undefined,
): UserAccountType | undefined {
  if (userTypeId == null) return undefined;
  return USER_TYPE_ID_TO_ACCOUNT_TYPE[userTypeId];
}

/**
 * Human-readable label for a numeric `userTypeId`, sourced from the canonical
 * `ACCOUNT_TYPE_LABELS` map so the same strings are used everywhere
 * (profile header, member rows, connection lists, …).
 */
export function getUserTypeLabel(userTypeId: number | null | undefined): string | undefined {
  const accountType = getAccountTypeFromUserTypeId(userTypeId);
  return accountType ? ACCOUNT_TYPE_LABELS[accountType] : undefined;
}
